import type { PlaybookCollection as PlaybookCollectionEnum } from '@api/models';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import type { Ref } from 'vue';
import { computed } from 'vue';

import type {
  CollectionStats,
  CreateKnowledgeInput,
  Knowledge,
  KnowledgeProposal,
  KnowledgeVersion,
  PlaybookExtraction,
  PlaybookStats,
  ProposalStatus,
  UpdateKnowledgeInput,
} from '@stuut/api-sdk/models';

import { sdk } from '@/sdk';

export type PlaybookCollection = `${PlaybookCollectionEnum}` | 'templates';

// Backend returns `pending_proposal_count` per collection; SDK type is stale.
type CollectionStatsExt = CollectionStats & { pendingProposalCount?: number };
export type PlaybookStatsExt = Omit<PlaybookStats, 'collections'> & {
  collections?: CollectionStatsExt[];
};

// 'templates' isn't a real backend collection; drop it before SDK calls.
type SdkCollection = `${PlaybookCollectionEnum}`;
const toSdkCollection = (c: PlaybookCollection | null | undefined): SdkCollection | undefined =>
  c && c !== 'templates' ? (c as SdkCollection) : undefined;

export const PLAYBOOK_KEYS = {
  knowledge: (
    collection?: string | null,
    query?: string | null,
    customerId?: string | null,
    segmentId?: string | null,
  ) =>
    [
      'playbook',
      'knowledge',
      collection ?? '__all__',
      query ?? '',
      customerId ?? '',
      segmentId ?? '',
    ] as const,
  article: (articleId?: string) => ['playbook', 'article', articleId] as const,
  search: (query: string, collection?: string | null) =>
    ['playbook', 'search', query, collection ?? '__all__'] as const,
  proposals: (status?: string | null, collection?: string | null) =>
    ['playbook', 'proposals', status ?? '__all__', collection ?? '__all__'] as const,
  proposal: (proposalId?: string) => ['playbook', 'proposal', proposalId] as const,
  extractions: ['playbook', 'extractions'] as const,
  stats: ['playbook', 'stats'] as const,
};

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function usePlaybookKnowledge(
  collection?: Ref<PlaybookCollection | null | undefined>,
  query?: Ref<string | null | undefined>,
  customerId?: Ref<string | null | undefined>,
  segmentId?: Ref<string | null | undefined>,
) {
  return useQuery<Knowledge[]>({
    queryKey: computed(
      () =>
        [
          'playbook',
          'knowledge',
          collection?.value ?? '__all__',
          query?.value ?? '',
          customerId?.value ?? '',
          segmentId?.value ?? '',
        ] as const,
    ),
    queryFn: () => {
      const params: {
        collection?: SdkCollection;
        query?: string;
        customerId?: string;
        segmentId?: string;
      } = {};
      const sdkCollection = toSdkCollection(collection?.value);
      if (sdkCollection) params.collection = sdkCollection;
      if (query?.value) params.query = query.value;
      if (customerId?.value) params.customerId = customerId.value;
      if (segmentId?.value) params.segmentId = segmentId.value;
      return sdk.aether.playbook.knowledge.list(Object.keys(params).length ? params : undefined);
    },
    retry: 1,
  });
}

export function usePlaybookArticleVersions(articleId: Ref<string | undefined>) {
  return useQuery<KnowledgeVersion[]>({
    queryKey: computed(() => ['playbook', 'article', articleId.value, 'versions'] as const),
    queryFn: () => sdk.aether.playbook.knowledge.listVersions({ articleId: articleId.value! }),
    enabled: computed(() => !!articleId.value),
    retry: 1,
  });
}

export function usePlaybookArticle(articleId: Ref<string | undefined>) {
  return useQuery<Knowledge>({
    queryKey: computed(() => PLAYBOOK_KEYS.article(articleId.value)),
    queryFn: () => sdk.aether.playbook.knowledge.get({ articleId: articleId.value! }),
    enabled: computed(() => !!articleId.value),
    retry: 1,
  });
}

export function usePlaybookSearch(
  query: Ref<string>,
  collection?: Ref<PlaybookCollection | null | undefined>,
) {
  return useQuery<Knowledge[]>({
    queryKey: computed(() => PLAYBOOK_KEYS.search(query.value, collection?.value)),
    queryFn: () =>
      sdk.aether.playbook.knowledge.list({
        query: query.value,
        collection: toSdkCollection(collection?.value),
      }),
    enabled: computed(() => query.value.length >= 2),
    retry: 1,
  });
}

export const PROPOSALS_PAGE_SIZE = 25;

export function usePlaybookProposal(proposalId: Ref<string | undefined>) {
  return useQuery<KnowledgeProposal>({
    queryKey: computed(() => PLAYBOOK_KEYS.proposal(proposalId.value)),
    queryFn: () => sdk.aether.playbook.proposals.get({ proposalId: proposalId.value! }),
    enabled: computed(() => !!proposalId.value),
    retry: 1,
  });
}

export function usePlaybookProposals(
  status?: Ref<ProposalStatus | null | undefined>,
  collection?: Ref<PlaybookCollection | null | undefined>,
) {
  return useInfiniteQuery({
    queryKey: computed(() => PLAYBOOK_KEYS.proposals(status?.value, collection?.value)),
    queryFn: ({ pageParam }) => {
      const sdkCollection = toSdkCollection(collection?.value);
      return sdk.aether.playbook.proposals.list({
        ...(status?.value ? { status: status.value } : {}),
        ...(sdkCollection ? { collection: sdkCollection } : {}),
        limit: PROPOSALS_PAGE_SIZE,
        offset: pageParam,
      } as any);
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage: KnowledgeProposal[], _allPages, lastPageParam: number) =>
      lastPage.length < PROPOSALS_PAGE_SIZE ? undefined : lastPageParam + PROPOSALS_PAGE_SIZE,
    retry: 1,
  });
}

export function usePlaybookStats() {
  return useQuery<PlaybookStatsExt>({
    queryKey: PLAYBOOK_KEYS.stats,
    queryFn: () => sdk.aether.playbook.getStats() as Promise<PlaybookStatsExt>,
    retry: 1,
  });
}

export function usePlaybookExtractions() {
  return useQuery<PlaybookExtraction[]>({
    queryKey: PLAYBOOK_KEYS.extractions,
    queryFn: () => sdk.aether.playbook.extractions.list(),
    retry: 1,
    refetchInterval: (q) => {
      const terminal = new Set(['complete', 'failed']);
      const hasActive = q.state.data?.some((e) => !terminal.has(e.status));
      return hasActive ? 5000 : false;
    },
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useCreatePlaybookKnowledge() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ body }: { body: CreateKnowledgeInput }) =>
      sdk.aether.playbook.knowledge.create(body),
    onSuccess: (created) => {
      // Prime the article-detail cache so the destination page renders the
      // article instantly after the post-publish redirect.
      queryClient.setQueryData(PLAYBOOK_KEYS.article(created.id), created);
      // Prepend to every active list cache so the sidebar surfaces the new
      // article immediately — refetches don't always return just-created rows
      // (eventual consistency on the list endpoint, scope filters, etc.).
      queryClient.setQueriesData<Knowledge[] | undefined>(
        { queryKey: ['playbook', 'knowledge'] },
        (old) => (old ? [created, ...old] : [created]),
      );
      queryClient.invalidateQueries({ queryKey: ['playbook', 'knowledge'] });
      queryClient.invalidateQueries({ queryKey: ['playbook', 'topics'] });
    },
  });
}

export function useRevisePlaybookKnowledge() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ articleId, body }: { articleId: string; body: UpdateKnowledgeInput }) =>
      sdk.aether.playbook.knowledge.update({ articleId, body }),
    onSuccess: (data, variables) => {
      // Sync cache update so the view-mode TextEditor remounts with fresh
      // content immediately after save (refetch is async and races the
      // isEditing toggle, leaving stale content visible until refresh).
      queryClient.setQueryData(PLAYBOOK_KEYS.article(variables.articleId), data);
      queryClient.invalidateQueries({
        queryKey: ['playbook', 'article', variables.articleId, 'versions'],
      });
      queryClient.invalidateQueries({ queryKey: ['playbook', 'knowledge'] });
    },
  });
}

export function useArchivePlaybookKnowledge() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (articleId: string) => sdk.aether.playbook.knowledge.archive({ articleId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['playbook', 'knowledge'] });
    },
  });
}

export function useApproveProposal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      proposalId,
      reviewedBy,
      reviewNotes,
    }: {
      proposalId: string;
      reviewedBy: string;
      reviewNotes?: string;
    }) =>
      sdk.aether.playbook.proposals.approve({
        proposalId,
        body: { reviewedBy, reviewNotes },
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['playbook', 'proposals'] });
      queryClient.invalidateQueries({ queryKey: PLAYBOOK_KEYS.proposal(variables.proposalId) });
      queryClient.invalidateQueries({ queryKey: ['playbook', 'knowledge'] });
    },
  });
}

export function useRejectProposal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      proposalId,
      reviewedBy,
      reviewNotes,
    }: {
      proposalId: string;
      reviewedBy: string;
      reviewNotes?: string;
    }) =>
      sdk.aether.playbook.proposals.reject({
        proposalId,
        body: { reviewedBy, reviewNotes },
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['playbook', 'proposals'] });
      queryClient.invalidateQueries({ queryKey: PLAYBOOK_KEYS.proposal(variables.proposalId) });
    },
  });
}

export function useCreateExtraction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      fileId,
      createdBy,
      collection,
    }: {
      fileId: string;
      createdBy?: string;
      collection?: PlaybookCollection;
    }) =>
      sdk.aether.playbook.extractions.create({
        fileId,
        createdBy,
        collection: toSdkCollection(collection),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PLAYBOOK_KEYS.extractions });
    },
  });
}
